/**
 * Desktop PDP/bundle: pin gallery within the full-height media column through support
 * (“Have a question?”), then translate to release before overlapping the footer.
 */
const DESKTOP_MQ = '(min-width: 881px)';

/**
 * @param {HTMLElement} boundary
 * @returns {HTMLElement | null}
 */
function getStickyMediaEl(boundary) {
  return boundary.querySelector('.product-information__media-sticky');
}

/**
 * @param {HTMLElement} boundary
 */
function getStickyEndBottom(boundary) {
  const end =
    boundary.querySelector('.pdp-sticky-scroll-end') ||
    boundary.querySelector('.product-details');
  if (end instanceof HTMLElement) {
    return end.getBoundingClientRect().bottom;
  }
  return boundary.getBoundingClientRect().bottom;
}

/**
 * @param {HTMLElement} boundary
 * @param {HTMLElement} stickyMedia
 */
function updateStickyMediaOffset(boundary, stickyMedia) {
  if (!window.matchMedia(DESKTOP_MQ).matches) {
    stickyMedia.style.transform = '';
    return;
  }

  const top = Number.parseFloat(getComputedStyle(stickyMedia).top) || 0;
  const endBottom = getStickyEndBottom(boundary);
  const height = stickyMedia.getBoundingClientRect().height;
  const bottomLimit = endBottom - height;
  const y = Math.min(0, bottomLimit - top);

  stickyMedia.style.transform = y < 0 ? `translate3d(0, ${y}px, 0)` : '';
}

class ProductStickyBoundary extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    const stickyMedia = getStickyMediaEl(this);
    if (!(stickyMedia instanceof HTMLElement)) return;

    const mq = window.matchMedia(DESKTOP_MQ);
    const schedule = () => {
      if (this.#frame) cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(() => {
        updateStickyMediaOffset(this, stickyMedia);
      });
    };

    const onMqChange = () => {
      if (!mq.matches) stickyMedia.style.transform = '';
      schedule();
    };

    window.addEventListener('scroll', schedule, { passive: true, signal });
    window.addEventListener('resize', schedule, { passive: true, signal });
    mq.addEventListener('change', onMqChange, { signal });

    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(schedule);
      ro.observe(this);
      ro.observe(stickyMedia);
      const details = this.querySelector('.product-details');
      if (details instanceof HTMLElement) ro.observe(details);
      signal.addEventListener('abort', () => ro.disconnect());
    }

    schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }
}

if (!customElements.get('product-sticky-boundary')) {
  customElements.define('product-sticky-boundary', ProductStickyBoundary);
}
